<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useLayoutComponents } from '@/composables/layout.js'
import { useToast } from '@/composables/use-toast'
import { useStorage } from '@/composables/use-storage'
import axios from 'axios'

const { TextInput, Checkbox, PrimaryButton, InputError } = useLayoutComponents()
const { addToast } = useToast()
const { getUserInfo } = useStorage()
const userInfo = ref(getUserInfo())

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    bidActive: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['close'])

const loading = ref(false)
const bidAmount = ref('')
const isConfirmed = ref(false)

// Set tomorrow as default expiration date
const tomorrow = new Date()
tomorrow.setDate(tomorrow.getDate() + 1)
const bidExpiration = ref(tomorrow.toISOString().split('T')[0])

const isValidBid = computed(() => {
    const amount = parseFloat(bidAmount.value)
    return !isNaN(amount) && amount >= props.bidActive.minimum_bid && isConfirmed.value
})

const handleBidSubmit = async () => {
    if (!isValidBid.value) return
    
    loading.value = true
    try {
        const response = await axios.post('/api/bids', {
            listing_id: props.bidActive.id,
        bid_amount: bidAmount.value,
        bidder_id: userInfo.value.id,
        owner_id: props.bidActive?.datasets[0]?.dataset?.seller?.user?.id,
        currency: 'USDC',
        expires_at: bidExpiration.value
        })

        if (response.data.status === 200 || response.data.status === 201) {
            addToast('Bid submitted successfully', 'success')
            emit('close')
        } else {
            addToast(response.data.message, 'error')
        }
    } catch (error) {
        console.error(error)
        addToast(error.response?.data?.message || 'Error submitting bid', 'error')
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div id="Bid" :class="isOpen ? 'fixed' : 'hidden'" tabindex="-1"
        class="z-50 flex items-center justify-center overflow-hidden m-auto bg-echoes-background-black/50 inset-0 backdrop-blur-sm">
        <div class="relative w-full h-auto max-w-md p-4 animate-slideIn">
            <div class="relative bg-echoes-background-gray rounded-[21px] shadow-xl border border-echoes-border-light">
                <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                    <h5 class="text-xl font-semibold">Place Bid</h5>
                    <button type="button" @click="$emit('close')"
                        title="Close"
                        class="text-white bg-transparent hover:bg-white/10 hover:text-red-600 transition-colors duration-300 ease-in-out rounded-lg text-sm p-1.5 ms-auto inline-flex items-center">
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clip-rule="evenodd"></path>
                        </svg>
                    </button>
                </div>
                <div class="p-6">
                    <div class="format-notice">
                        <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="info-circle"><path fill="currentColor" d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"></path></svg>
                        <p class="whitespace-nowrap">This dataset has a minimum bid of <span class="text-blue-500 font-bold">${{ bidActive.minimum_bid }}</span></p>
                    </div>
                    <div class="mb-4">
                        <div class="field-input-group-item-label-container">
                            <div class="field-input-group-item-label font-medium flex items-center gap-1">Bid Amount <p class="text-sm text-gray-400">(Required)</p></div>
                            <InputError class="label-error" />
                        </div>
                        <TextInput v-model="bidAmount" placeholder="Enter at least the minimum bid" type="number" />
                    </div>

                    <div class="mb-4">
                        <div class="field-input-group-item-label-container">
                            <div class="field-input-group-item-label font-medium flex items-center gap-1">Bid Expiration <p class="text-sm text-gray-400">(Optional)</p></div>
                            <InputError class="label-error" />
                        </div>
                        <TextInput v-model="bidExpiration" type="date" />
                    </div>
                    
                    <div class="flex items-start gap-2 mb-6">
                        <Checkbox v-model="isConfirmed" required />
                        <div class="flex flex-col">
                            <span for="confirmBid" class="text-sm text-echoes-primary font-semibold">Confirm Bid</span>
                            <span class="text-xs text-gray-500">I acknowledge this bid is binding and cannot be canceled</span>
                            <a href="https://portexai.com/tou" target="_blank" class="text-xs text-white/80 hover:text-echoes-primary underline animate-ease-in-out duration-300">Learn more</a>
                        </div>
                    </div>

                    <PrimaryButton 
                        @click="handleBidSubmit"
                        :disabled="loading || !isValidBid"
                        class="w-full py-2 text-sm rounded-[13px]"
                        :loading="loading">
                        Submit Bid
                    </PrimaryButton>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="sass" scoped>
.format-notice {
    @apply text-sm px-4 flex items-center gap-2 py-2 mb-4 bg-blue-500/20 border border-blue-500/50 text-blue-500 rounded-lg text-center;
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.field-input-group-item-label {
    @apply text-sm text-gray-200;
}

.field-input-group-item-label-container {
    @apply flex items-center mb-2 w-fit gap-2;
}
</style> 